import React, { useState } from 'react';
import { styled, Grid, Box, Typography } from '@mui/material';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useTextInput, useNumberInput } from 'hooks/forms';
import { Section, Header, Content } from 'components/ui/forms';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { TextField, ProvinceSelector, TextArea } from 'components/ui/inputs';
import { useNotification } from 'context/notification';
import { submitBusinessDetails } from 'pages/stripeOnboarding/StripeOnboardingAPI';
import { ActionButton } from 'components/ui/buttons';

export default function Business(props) {
    const { hostId, connectId, business, readOnly, onUpdate } = props;

    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);

    const { notify } = useNotification();

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const handleSubmit = (values) => {
        setLoading(true);

        submitBusinessDetails(hostId, connectId, values)
            .then(({ business, requirements }) => {
                notify.success('Updated Business Details');
                setModalOpen(false);
                onUpdate(business, requirements);
            })
            .catch((error) => notify.error(error.message))
            .finally(() => setLoading(false));
    };

    return (
        <Section>
            <Header>About your Business</Header>
            <Typography>
                Please provide the exact details used in your official business registration. These
                details will be matched by our payment partner against public registries to confirm
                your organization's identity and ensure compliance.
            </Typography>
            <Content>
                {business.complete ? (
                    <BusinessCard business={business} onEdit={handleModalOpen} />
                ) : (
                    <BusinessForm
                        editMode={false}
                        readOnly={readOnly}
                        business={business}
                        onSubmit={handleSubmit}
                        onClose={handleModalClose}
                        loading={loading}
                    />
                )}
            </Content>

            {modalOpen && (
                <Dialog open onClose={handleModalClose}>
                    <DialogTitle>Edit Business Details</DialogTitle>
                    <DialogContent>
                        <BusinessForm
                            editMode={true}
                            readOnly={readOnly}
                            business={business}
                            onSubmit={handleSubmit}
                            onClose={handleModalClose}
                            loading={loading}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Section>
    );
}

function BusinessCard(props) {
    const { business, onEdit } = props;

    return (
        <Box
            border={'1px solid #888'}
            borderRadius="8px"
            p={4}
            pl={2}
            pr={2}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}
        >
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <OrgIcon />
                <Box ml={2}>
                    <BusinessName>{business.legalName}</BusinessName>
                    <BusinessEmail>{business.registration}</BusinessEmail>
                </Box>
            </Box>
            <Box>
                <ActionButton onClick={onEdit}>Edit</ActionButton>
            </Box>
        </Box>
    );
}

function BusinessForm(props) {
    const { editMode, readOnly, business, onSubmit, onClose, loading } = props;

    const { notify } = useNotification();

    const legalName = useTextInput({ disabled: readOnly, defaultValue: business.legalName });
    const registration = useNumberInput({
        disabled: readOnly,
        defaultValue: business.registration,
        integerOnly: true,
    }); // non-profit organization
    const taxId = useTextInput({
        disabled: readOnly,
        defaultValue: '',
        integerOnly: true,
        maxLength: 15,
        noSpaces: true,
        format: 'alphanumeric',
    }); // registered charity or private corporation
    const name = useTextInput({ disabled: readOnly, defaultValue: business.name });
    const streetAddress = useTextInput({
        disabled: readOnly,
        defaultValue: business.streetAddress,
    });
    const unitNumber = useTextInput({ disabled: readOnly, defaultValue: business.unitNumber });
    const city = useTextInput({ disabled: readOnly, defaultValue: business.city });
    const province = useTextInput({ disabled: readOnly, defaultValue: business.province });
    const postalCode = useTextInput({ disabled: readOnly, defaultValue: business.postalCode });
    const phoneNumber = useNumberInput({
        disabled: readOnly,
        defaultValue: business.phoneNumber,
        integerOnly: true,
    });
    const website = useTextInput({ disabled: readOnly, defaultValue: business.website });
    const description = useTextInput({ disabled: readOnly, defaultValue: business.description });

    const handleSubmit = () => {
        let formError = false;

        // *** Check for Empty Fields *** //

        // unitNumber and name (business name) are excepted
        [
            legalName,
            streetAddress,
            city,
            province,
            postalCode,
            phoneNumber,
            website,
            description,
        ].forEach((field) => {
            if (field.value.trim().length === 0) {
                field.setError('This field cannot be empty');
                formError = true;
            }
        });

        if (
            business.structure === 'private_corporation' ||
            business.structure === 'registered_charity'
        ) {
            if (business.taxIdProvided === false) {
                // Registered Charity or Corporation - Validate Tax ID (Only on first submission, Tax ID can't be updated)
                if (taxId.value.trim().length === 0) {
                    taxId.setError('This field cannot be empty');
                    formError = true;
                }
            }
        }

        if (business.businessType === 'non_profit' && business.structure === null) {
            // Non-Profit Organization - Validate Registration Number
            if (registration.value.trim().length === 0) {
                registration.setError('This field cannot be empty');
                formError = true;
            }
        }

        // *** Form Invalid *** //

        if (formError) {
            notify.error('Please fill out all fields');
            return;
        }

        // *** Submit Form *** //

        onSubmit({
            legalName: legalName.value.trim(),
            taxId: taxId.value.trim(),
            registration: registration.value.trim(),
            name: name.value.trim(),
            streetAddress: streetAddress.value.trim(),
            unitNumber: unitNumber.value.trim(),
            city: city.value.trim(),
            province: province.value.trim(),
            postalCode: postalCode.value.trim(),
            phoneNumber: phoneNumber.value.trim(),
            website: website.value.trim(),
            description: description.value.trim(),
        });
    };

    return (
        <Box>
            <Box mb={6}>
                <SectionHeader>Legal Business Name</SectionHeader>
                <SectionSubheader>
                    Enter the exact name shown on your official business registration.
                </SectionSubheader>
                <TextField label="Legal Business Name" {...legalName.formProps} />
            </Box>
            {business.structure === 'registered_charity' && (
                <Box mb={6} mt={6}>
                    <SectionHeader>CRA Program Account Number</SectionHeader>
                    {business.taxIdProvided ? (
                        <SectionSubheader>
                            Your account number has been saved. Please contact FrontDoor+ if you
                            need this number changed.
                        </SectionSubheader>
                    ) : (
                        <SectionSubheader>
                            This is made up of your 9 digit business number, plus 2 letters and 4
                            digits attached to the business number.
                        </SectionSubheader>
                    )}
                    {business.taxIdProvided ? (
                        <TextField disabled label="Registration Number Saved" />
                    ) : (
                        <TextField
                            label="Registration Number"
                            {...taxId.formProps}
                            placeholder="123456789 RR 1234"
                        />
                    )}
                </Box>
            )}

            {business.structure === 'private_corporation' && (
                <Box mb={6} mt={6}>
                    <SectionHeader>Registration Number</SectionHeader>
                    {business.taxIdProvided ? (
                        <SectionSubheader>
                            Your registration number has been saved. Please contact FrontDoor+ if
                            you need this number changed.
                        </SectionSubheader>
                    ) : (
                        <SectionSubheader>
                            Provide your official registry ID, 7 digit corporation number, or 10
                            digit Québec Enterprise Number (NEQ).
                        </SectionSubheader>
                    )}
                    {business.taxIdProvided ? (
                        <TextField disabled label="Registration Number Saved" />
                    ) : (
                        <TextField
                            label="Registration Number"
                            {...taxId.formProps}
                            placeholder="123456789"
                        />
                    )}
                </Box>
            )}

            {business.businessType === 'non_profit' && business.structure === null && (
                <Box mb={6} mt={6}>
                    <SectionHeader>Registration Number</SectionHeader>
                    <SectionSubheader>
                        Provide your official registry ID, 7 digit corporation number, or 10 digit
                        Québec Enterprise Number (NEQ).
                    </SectionSubheader>
                    <TextField label="Registration Number" {...registration.formProps} />
                </Box>
            )}

            <Box mb={6} mt={6}>
                <SectionHeader>Business Name [optional]</SectionHeader>
                <SectionSubheader>
                    If you operate under a different name than your legal name, add it here. This is
                    the name the public knows you by.
                </SectionSubheader>
                <TextField label="Business Name [optional]" {...name.formProps} />
            </Box>
            <Box mb={6} mt={6}>
                <SectionHeader>Business Address</SectionHeader>
                <SectionSubheader>
                    Use the address shown on your official business registration. It must match
                    exactly.
                </SectionSubheader>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField label="Street Address" {...streetAddress.formProps} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Apartment/unit/other" {...unitNumber.formProps} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="City" {...city.formProps} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <ProvinceSelector region="CA" {...province.formProps} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Postal Code" {...postalCode.formProps} />
                    </Grid>
                </Grid>
            </Box>
            <Box mb={6} mt={6}>
                <SectionHeader>Business Phone Number</SectionHeader>
                <SectionSubheader>
                    Enter a primary phone number for your organization. This is not displayed to the
                    public.
                </SectionSubheader>
                <TextField label="Business Phone Number" {...phoneNumber.formProps} />
            </Box>
            <Box mb={6} mt={6}>
                <SectionHeader>Business Website</SectionHeader>
                <SectionSubheader>
                    May be reviewed to confirm it aligns with the details above.
                </SectionSubheader>
                <TextField label="Website" {...website.formProps} />
            </Box>
            <Box mb={6} mt={6}>
                <SectionHeader>Business/Product Description</SectionHeader>
                <SectionSubheader>
                    Write a brief 1-2 sentence summary so our payment partner can understand your
                    organization's activities.
                </SectionSubheader>
                <TextArea label="Business Description" {...description.formProps} />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={4}>
                {editMode && (
                    <CancelButton disabled={loading} onClick={onClose}>
                        Cancel
                    </CancelButton>
                )}
                <SubmitButton
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={loading || readOnly}
                >
                    Save
                </SubmitButton>
            </Box>
        </Box>
    );
}

const OrgIcon = styled(BusinessIcon)(({ theme }) => ({
    fontSize: 52,
}));

const BusinessName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const BusinessEmail = styled(Typography)(({ theme }) => ({}));

const SectionHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontSize: 18,
}));

const SectionSubheader = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));
